import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import {
  CurrencyForm,
  emptyCurrencyForm,
  type CurrencyFormValue,
} from '../components/financeForms'

export interface Currency {
  currencyId: number
  currency: string
  currencyCode: string
  currencySymbol: string
  subunit: number
  isEnabled: boolean
  isDisplay: boolean
  isCrypto: boolean
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface CurrencyPage {
  currencies: Currency[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * CurrenciesPage is the currency catalogue — [Catalog].[CurrencyHub.Currency]
 * in the BsConnekto database.
 *
 * Three flags mean three different things and the table shows all three:
 * IsEnabled (the currency may be used), IsDisplay (it is offered in the UI) and
 * IsCrypto. Retiring a currency is the platform's own switch and leaves the
 * other two alone — so a restore brings back exactly what was there.
 */
export function CurrenciesPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Currency | null>(null)
  const [form, setForm] = useState<CurrencyFormValue>(emptyCurrencyForm)
  const [error, setError] = useState('')

  const params = { search, includeDisabled: String(includeDisabled), offset, limit: PAGE_SIZE }

  const { data, isLoading } = useQuery({
    queryKey: ['currencies', params],
    queryFn: async (): Promise<CurrencyPage> => {
      const res = await api.get('/currencies', { params })
      return res.data as CurrencyPage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['currencies'] })

  const openCreate = () => {
    setEditing(null)
    setForm(emptyCurrencyForm)
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (c: Currency) => {
    setEditing(c)
    setForm({
      currencyId: String(c.currencyId),
      currency: c.currency,
      currencyCode: c.currencyCode,
      currencySymbol: c.currencySymbol,
      subunit: String(c.subunit),
      isEnabled: c.isEnabled,
      isDisplay: c.isDisplay,
      isCrypto: c.isCrypto,
    })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        currency: form.currency.trim(),
        currencyCode: form.currencyCode.trim(),
        currencySymbol: form.currencySymbol.trim(),
        subunit: Number(form.subunit),
        isEnabled: form.isEnabled,
        isDisplay: form.isDisplay,
        isCrypto: form.isCrypto,
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/currencies/${editing.currencyId}`, body)
        : api.post('/currencies', { ...body, currencyId: Number(form.currencyId) })
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('currencies.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (c: Currency) =>
      c.isDisabledByPlatform
        ? api.post(`/currencies/${c.currencyId}/restore`)
        : api.delete(`/currencies/${c.currencyId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('currencies.errors.save'))),
  })

  const currencies = data?.currencies ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('currencies.title')}
        subtitle={t('currencies.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('currencies.newCurrency')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('currencies.editCurrency') : t('currencies.newCurrency')}
                  </DialogTitle>
                </DialogHeader>
                <CurrencyForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  isCreate={editing === null}
                  pending={save.isPending}
                  error={error}
                  submitLabel={editing ? t('common.save') : t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="currencies">
        <p>{t('currencies.help.line1')}</p>
        <p>{t('currencies.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('currencies.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      />

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('currencies.columns.id')}</TableHead>
                  <TableHead className="w-24">{t('currencies.columns.code')}</TableHead>
                  <TableHead>{t('currencies.columns.name')}</TableHead>
                  <TableHead className="w-20">{t('currencies.columns.symbol')}</TableHead>
                  <TableHead className="w-24">{t('currencies.columns.subunit')}</TableHead>
                  <TableHead className="w-56">{t('currencies.columns.flags')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {currencies.map((c) => (
                  <TableRow key={c.currencyId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.currencyId}
                    </TableCell>
                    <TableCell className="font-mono font-medium">
                      <Link to={`/currencies/${c.currencyId}`} className="hover:underline">
                        {c.currencyCode}
                      </Link>
                    </TableCell>
                    <TableCell>{c.currency}</TableCell>
                    <TableCell>{c.currencySymbol}</TableCell>
                    <TableCell className="font-mono text-xs">{c.subunit}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {c.isEnabled && (
                          <Badge variant="secondary">{t('currencies.columns.enabled')}</Badge>
                        )}
                        {c.isDisplay && (
                          <Badge variant="secondary">{t('currencies.columns.display')}</Badge>
                        )}
                        {c.isCrypto && (
                          <Badge variant="outline">{t('currencies.columns.crypto')}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={c.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(c)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={c.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(c)}
                          >
                            {c.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {currencies.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 8 : 7}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('currencies.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('currencies.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
